import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { company } from "@/data/landing";
import { fadeUp } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const subjects = [
    "General Inquiry",
    "Marketing Automation",
    "Full Stack Development",
    "Mobile App Development",
    "AI / Custom Software",
    "Partnership",
];

const initial = { name: "", email: "", phone: "", subject: "", message: "" };

const ContactInfo = ({ icon: Icon, label, value, href, testid }) => (
    <a
        href={href}
        data-testid={testid}
        className="flex items-start gap-4 p-5 rounded-xl bg-white/[0.03] border border-white/5 hover:bg-white/[0.06] hover:border-white/15 transition group"
    >
        <span className="h-11 w-11 rounded-lg bg-gradient-to-br from-cyan-400/20 to-violet-500/20 border border-white/10 grid place-items-center shrink-0 group-hover:scale-110 transition">
            <Icon className="h-5 w-5 text-cyan-300" strokeWidth={1.75} />
        </span>
        <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
                {label}
            </div>
            <div className="mt-1 text-white font-medium break-words">{value}</div>
        </div>
    </a>
);

const Contact = () => {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);

    const update = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.subject) {
            toast.error("Please select a subject");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Message received — we'll reply within 24 hours.");
            setForm(initial);
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                "Something went wrong. Please try again.";
            toast.error(typeof detail === "string" ? detail : "Submission failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="section py-24 md:py-32 bg-[#070708]/70"
        >
            <div
                className="orb"
                style={{
                    width: 520,
                    height: 520,
                    top: "10%",
                    right: "-200px",
                    background:
                        "radial-gradient(circle, rgba(0,229,255,0.20), transparent 60%)",
                }}
            />
            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
                <motion.div {...fadeUp} className="max-w-3xl mb-14">
                    <span className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-300">
                        07 / Contact
                    </span>
                    <h2
                        data-testid="contact-heading"
                        className="mt-4 font-display font-semibold text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
                    >
                        Let&apos;s build
                        <br />
                        <span className="text-grad-cyan">something legendary.</span>
                    </h2>
                </motion.div>

                <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
                    <motion.div {...fadeUp} className="lg:col-span-2 flex flex-col gap-4">
                        <ContactInfo
                            icon={Phone}
                            label="Call Us"
                            value={company.phone}
                            href={`tel:${company.phone.replace(/\s/g, "")}`}
                            testid="contact-info-phone"
                        />
                        <ContactInfo
                            icon={Mail}
                            label="Email Us"
                            value={company.email}
                            href={`mailto:${company.email}`}
                            testid="contact-info-email"
                        />
                        <ContactInfo
                            icon={MapPin}
                            label="Visit Us"
                            value={company.address}
                            href="#"
                            testid="contact-info-address"
                        />
                        <ContactInfo
                            icon={Clock}
                            label="Working Hours"
                            value="Mon – Sat · 10:00 AM – 7:00 PM IST"
                            href="#"
                            testid="contact-info-hours"
                        />
                    </motion.div>

                    <motion.form
                        {...fadeUp}
                        onSubmit={submit}
                        data-testid="contact-form"
                        className="lg:col-span-3 glass rounded-2xl p-8 md:p-10 space-y-5"
                        noValidate
                    >
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <Label className="text-zinc-300 text-xs font-mono uppercase tracking-[0.15em]">
                                    Full Name *
                                </Label>
                                <Input
                                    required
                                    value={form.name}
                                    onChange={update("name")}
                                    placeholder="Your name"
                                    data-testid="contact-name"
                                    className="mt-2 bg-white/[0.04] border-white/10 text-white placeholder:text-zinc-600 h-12 focus-visible:ring-cyan-400/40 focus-visible:border-cyan-400/50"
                                />
                            </div>
                            <div>
                                <Label className="text-zinc-300 text-xs font-mono uppercase tracking-[0.15em]">
                                    Email *
                                </Label>
                                <Input
                                    required
                                    type="email"
                                    value={form.email}
                                    onChange={update("email")}
                                    placeholder="you@company.com"
                                    data-testid="contact-email"
                                    className="mt-2 bg-white/[0.04] border-white/10 text-white placeholder:text-zinc-600 h-12 focus-visible:ring-cyan-400/40 focus-visible:border-cyan-400/50"
                                />
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <Label className="text-zinc-300 text-xs font-mono uppercase tracking-[0.15em]">
                                    Phone
                                </Label>
                                <Input
                                    value={form.phone}
                                    onChange={update("phone")}
                                    placeholder="+91"
                                    data-testid="contact-phone"
                                    className="mt-2 bg-white/[0.04] border-white/10 text-white placeholder:text-zinc-600 h-12 focus-visible:ring-cyan-400/40 focus-visible:border-cyan-400/50"
                                />
                            </div>
                            <div>
                                <Label className="text-zinc-300 text-xs font-mono uppercase tracking-[0.15em]">
                                    Subject *
                                </Label>
                                <Select
                                    value={form.subject}
                                    onValueChange={(v) => setForm((f) => ({ ...f, subject: v }))}
                                >
                                    <SelectTrigger
                                        data-testid="contact-subject"
                                        className="mt-2 bg-white/[0.04] border-white/10 text-white h-12 focus:ring-cyan-400/40 data-[placeholder]:text-zinc-600"
                                    >
                                        <SelectValue placeholder="Select a subject" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-zinc-950 border-white/10 text-white">
                                        {subjects.map((s) => (
                                            <SelectItem key={s} value={s} className="focus:bg-cyan-400/10">
                                                {s}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div>
                            <Label className="text-zinc-300 text-xs font-mono uppercase tracking-[0.15em]">
                                Message *
                            </Label>
                            <Textarea
                                required
                                rows={5}
                                value={form.message}
                                onChange={update("message")}
                                placeholder="Tell us about your project, timelines and what success looks like…"
                                data-testid="contact-message"
                                className="mt-2 bg-white/[0.04] border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-cyan-400/40 focus-visible:border-cyan-400/50"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="contact-submit"
                            className="btn-shine w-full inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-400 hover:bg-cyan-300 disabled:opacity-60 disabled:cursor-not-allowed text-black font-semibold text-base px-6 py-4 transition"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Sending…
                                </>
                            ) : (
                                <>
                                    Send Message
                                    <Send className="h-4 w-4" />
                                </>
                            )}
                        </button>
                        <p className="text-[11px] text-zinc-500 text-center">
                            We typically respond within 24 hours. Your data is 100%
                            confidential.
                        </p>
                    </motion.form>
                </div>
            </div>
        </section>
    );
};

export default Contact;
